/**
 * The one entry point a wizard has. A wizard owns no page of its own, so the link carries
 * nothing but the slug and rides whatever URL a campaign was already sending.
 */
export const WIZARD_PARAM = 'wizard';

/**
 * The kill switch. Off means no query, no overlay and no sign-in bounce, so switching it off
 * fully stops every link already circulating.
 */
export const FEATURE_WIZARDS_FLAG = 'feature_wizards';

/**
 * The signup wizard owns these two screens. Two stacked wizards would break the flow that
 * already lives there, so the host does not mount at all on them — no query, no event, no
 * parameter strip — and `OnboardingRedirect` keeps exactly the precedence it has today. When
 * the welcome redirect wins the race, nothing has been recorded and the link still works on a
 * later visit.
 */
const CARVE_OUT_PREFIXES = ['/welcome', '/onboarding'];

/**
 * Routes that already resolve a signed-out visitor without help: everything behind
 * `ProtectedRoute`, whose `AttributionRedirectToSignIn` calls the same no-argument
 * `redirectToSignIn()` this host would, plus the three auth routes that bounce on their own.
 *
 * Everything else needs the host to bounce — `/assistant`, `/shared/:chatId`, `/eval/:chatId`,
 * `/unsubscribe`, `/community/digest-mute`, the two debug routes, and any path matching no
 * route at all, which lands on `NotFound`. No landing path is validated anywhere, so a
 * mistyped path in a broadcast is a real arrival and must still reach sign-in.
 *
 * Listing the self-handled routes rather than the public ones is deliberate: a route added to
 * `App.tsx` and forgotten here costs one extra redirect to the URL `ProtectedRoute` was
 * building anyway, whereas a forgotten public route would silently kill the campaign click.
 */
const SELF_HANDLED_PREFIXES = [
  '/admin',
  '/art',
  '/art-marketing-autopilot',
  '/billing',
  '/calendar',
  '/chat',
  '/forgot-password',
  '/grow-audience',
  '/grow-your-audience',
  '/integrations',
  '/messages',
  '/my-artwork',
  '/network',
  '/notifications',
  '/onboarding',
  '/plans',
  '/post',
  '/profile',
  '/saved',
  '/settings',
  '/signin',
  '/signup',
  '/upload',
  '/welcome',
];

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function isCarveOutPath(pathname: string): boolean {
  return CARVE_OUT_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

export function handlesSignedOutItself(pathname: string): boolean {
  if (pathname === '/') return true;
  return SELF_HANDLED_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}
